import type { Gate, PermissionKey } from '@/data/permissions';
import { SETUP_STATUSES } from '@/data/types';
import { localToday } from './dates';

/**
 * One table per entity, mirroring the backend whitelists and normalizers (app.<entity>_clean and the
 * app.norm_* helpers, read live on 2026-09-23). The client check is a courtesy that catches a typo on
 * blur; the server stays the judge, and its 22023 comes back mapped to the same field (lib/rpcErrors).
 */

export type FieldKind =
  | 'text'
  | 'longText'
  | 'authority'
  | 'ein'
  | 'state'
  | 'postal'
  | 'email'
  | 'phone'
  | 'vin'
  | 'int'
  | 'year'
  | 'num'
  | 'uuid'
  | 'website'
  | 'date'
  | 'license'
  | 'enum'
  | 'prefix'
  /** A checkbox: 'true' or '' in the form, a JSON boolean on the wire (spec factoring). */
  | 'bool';

export interface FieldSpec {
  /** The backend field key (snake_case), also the form-state key. */
  key: string;
  /** i18n key of the label (`fields.<camelCase>`). */
  label: string;
  kind: FieldKind;
  required?: boolean;
  /** Max length (text kinds). */
  max?: number;
  /** Range (number kinds). */
  min?: number;
  maxValue?: number;
  /** Max decimals (`num`). */
  scale?: number;
  /** Allowed values (`enum`). */
  options?: readonly string[];
  /** A gate stricter than the entity's own (e.g. carrier terms, owner links). */
  gate?: PermissionKey;
}

export type EntityName = 'owner' | 'carrier' | 'truck' | 'driver' | 'broker' | 'broker_setup' | 'load' | 'stop' | 'factoring';

export interface EntitySpec {
  entity: EntityName;
  /** Gate for fields on create (the create button carries it too). */
  createGate: Gate;
  /** Gate for fields on update. */
  updateGate: Gate;
  fields: FieldSpec[];
}

export interface ValidationContext {
  /** The agency's IANA zone: "today" and "this year" are cut there, never in the browser zone. */
  timeZone: string;
  /** Injected clock for tests; defaults to now. */
  now?: Date;
}

export interface FieldError {
  /** i18n key under `validation.` */
  key: string;
  values?: Record<string, string | number>;
}

export type FormValues = Record<string, string>;
export type Can = (gate: Gate) => boolean;

/** `minimum_rate_per_mile` -> `fields.minimumRatePerMile`. */
export function fieldLabelKey(key: string): string {
  return `fields.${key.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase())}`;
}

function f(key: string, kind: FieldKind, extra: Omit<Partial<FieldSpec>, 'key' | 'kind' | 'label'> = {}): FieldSpec {
  return { key, label: fieldLabelKey(key), kind, ...extra };
}

const text = (key: string, max: number, extra: Omit<Partial<FieldSpec>, 'key' | 'kind' | 'label'> = {}) =>
  f(key, 'text', { max, ...extra });

const address = [
  text('address_line1', 200),
  text('address_line2', 200),
  text('city', 100),
  f('state', 'state'),
  f('postal_code', 'postal', { max: 10 }),
];

/** Cargo type suggestions for the truck form's datalist (free text is allowed). */
export const CARGO_TYPES = [
  'dry_van',
  'reefer',
  'flatbed',
  'step_deck',
  'power_only',
  'hotshot',
  'box_truck',
  'tanker',
  'car_hauler',
  'other',
] as const;

export const ENTITY_SPECS: Record<EntityName, EntitySpec> = {
  owner: {
    entity: 'owner',
    createGate: 'owner.manage',
    updateGate: 'owner.manage',
    fields: [
      text('legal_name', 200, { required: true }),
      text('dba', 200),
      f('ein', 'ein'),
      ...address,
      text('contact_name', 200),
      f('contact_phone', 'phone', { max: 32 }),
      f('contact_email', 'email', { max: 254 }),
      f('notes', 'longText', { max: 4000 }),
    ],
  },
  carrier: {
    entity: 'carrier',
    createGate: 'carrier.create',
    updateGate: 'carrier.edit',
    fields: [
      text('legal_name', 200, { required: true }),
      text('dba', 200),
      f('mc_number', 'authority'),
      f('dot_number', 'authority'),
      f('ein', 'ein'),
      ...address,
      text('contact_name', 200),
      f('contact_phone', 'phone', { max: 32 }),
      f('contact_email', 'email', { max: 254 }),
      f('payment_terms_days', 'int', { min: 0, maxValue: 120, gate: 'carrier.edit_terms' }),
      // invoice-pdf (0145, app.carrier_clean): who gets the weekly invoice, in which language, and the carrier's
      // own broker invoice numbering. spec factoring (0201): the factoring company is an entity the carrier
      // points to; the four legacy factoring_* keys are refused by the server.
      f('billing_email', 'email', { max: 254 }),
      f('document_locale', 'enum', { options: ['en', 'es'] }),
      f('factoring_id', 'uuid'),
      f('broker_invoice_prefix', 'prefix', { max: 10 }),
      f('broker_invoice_next', 'int', { min: 1, maxValue: 999999999 }),
      f('minimum_rate_per_mile', 'num', { min: 0, maxValue: 99.99, scale: 2, gate: 'carrier.edit_terms' }),
      f('owner_id', 'uuid', { gate: 'owner.manage' }),
      f('notes', 'longText', { max: 4000 }),
    ],
  },
  truck: {
    entity: 'truck',
    createGate: 'truck.manage',
    updateGate: 'truck.manage',
    fields: [
      text('unit_number', 20, { required: true }),
      f('vin', 'vin'),
      f('year', 'year'),
      text('make', 60),
      text('model', 60),
      text('plate', 15),
      f('plate_state', 'state'),
      text('vehicle_type', 40),
      text('cargo_type', 40),
      f('max_weight', 'int', { min: 1, maxValue: 200000 }),
      f('length_ft', 'num', { min: 0.01, maxValue: 100, scale: 2 }),
      f('width_ft', 'num', { min: 0.01, maxValue: 100, scale: 2 }),
      f('height_ft', 'num', { min: 0.01, maxValue: 100, scale: 2 }),
      f('owner_id', 'uuid', { gate: 'owner.manage' }),
    ],
  },
  driver: {
    entity: 'driver',
    createGate: 'driver.edit',
    updateGate: 'driver.edit',
    fields: [
      text('name', 120, { required: true }),
      f('phone', 'phone', { max: 32 }),
      f('email', 'email', { max: 254 }),
      f('license_number', 'license', { max: 32 }),
      f('license_state', 'state'),
      f('notes', 'longText', { max: 4000 }),
    ],
  },
  broker: {
    entity: 'broker',
    createGate: 'broker.manage',
    updateGate: 'broker.manage',
    fields: [
      text('name', 200, { required: true }),
      f('mc_number', 'authority'),
      f('dot_number', 'authority'),
      ...address,
      f('phone', 'phone', { max: 32 }),
      f('email', 'email', { max: 254 }),
      // invoice-pdf (0145, app.broker_clean): the accounts payable address the broker invoice goes to.
      f('billing_email', 'email', { max: 254 }),
      f('website', 'website', { max: 300 }),
      f('notes', 'longText', { max: 4000 }),
      // audit-c (0318, app.broker_clean): the broker's own free time at a stop (blank = the agency's) and what it
      // pays per hour of detention, in DOLLARS (stored as detention_hourly_rate_cents).
      f('free_time_minutes', 'int', { min: 0, maxValue: 1440 }),
      f('detention_hourly_rate', 'num', { scale: 2, min: 0, maxValue: 1000 }),
    ],
  },
  // spec factoring (0201, app.factoring_clean): the agency's factoring companies. Admin and accounting write.
  factoring: {
    entity: 'factoring',
    createGate: 'factoring.manage',
    updateGate: 'factoring.manage',
    fields: [
      text('name', 200, { required: true }),
      f('remit_address', 'longText', { max: 300 }),
      f('phone', 'phone', { max: 32 }),
      f('email', 'email', { max: 254 }),
      f('noa_on_file', 'bool'),
      f('noa_received_on', 'date'),
      f('notes', 'longText', { max: 4000 }),
    ],
  },
  broker_setup: {
    entity: 'broker_setup',
    createGate: 'broker_setup.manage',
    updateGate: 'broker_setup.manage',
    fields: [
      f('status', 'enum', { options: SETUP_STATUSES, required: true }),
      f('setup_date', 'date'),
      f('notes', 'longText', { max: 4000 }),
    ],
  },
  // portal-loads (design 6.2): load_create and load_stop_* do not normalize (F14), so the client trims,
  // upper-cases the state and checks these kinds before sending; the server stays the judge.
  load: {
    entity: 'load',
    createGate: 'load.run',
    updateGate: 'load.run',
    fields: [
      text('load_number', 30),
      text('broker_reference', 60),
      text('commodity', 120),
      f('weight', 'int', { min: 1, maxValue: 200000 }),
      // acela-reports (0177): the same bounds as a truck's length.
      f('length_ft', 'num', { min: 0.01, maxValue: 100, scale: 2 }),
      f('loaded_miles', 'int', { min: 0, maxValue: 20000 }),
      f('deadhead_miles', 'int', { min: 0, maxValue: 20000 }),
      f('notes', 'longText', { max: 4000 }),
    ],
  },
  stop: {
    entity: 'stop',
    createGate: 'load.run',
    updateGate: 'load.run',
    fields: [
      text('company', 120),
      text('address_line1', 200),
      text('address_line2', 200),
      text('city', 100, { required: true }),
      f('state', 'state', { required: true }),
      f('postal_code', 'postal', { max: 10 }),
      text('contact_name', 120),
      f('contact_phone', 'phone'),
      text('reference_number', 60),
      f('notes', 'longText', { max: 4000 }),
    ],
  },
};

/**
 * The fields of the team and ledger dialogs (invite, profile, payment, receipt, mark invoiced, agency
 * rules) that a 22023 `<field> ...` message can name. Not entity specs: those dialogs build their own
 * forms. Each has a `fields.<camelCase>` label.
 */
export const DIALOG_FIELD_KEYS: ReadonlySet<string> = new Set([
  'display_name',
  'email',
  'phone',
  'locale',
  'reference',
  'notes',
  'invoice_number',
  'invoiced_on',
  'due_on',
  'paid_on',
  'factoring_company',
  'factored',
  'broker_terms_days',
  // invoice-pdf: the company profile (agency_profile_update) and the email recipient of the send dialogs.
  'payment_instructions',
  'invoice_note',
  'to',
]);

/** Every field-spec key of every entity, plus the dialog fields (the rpcErrors `<field> ...` rule keys on these). */
export const ALL_FIELD_KEYS: ReadonlySet<string> = new Set([
  ...Object.values(ENTITY_SPECS).flatMap((s) => s.fields.map((x) => x.key)),
  ...DIALOG_FIELD_KEYS,
]);

export function fieldOf(spec: EntitySpec, key: string): FieldSpec {
  const field = spec.fields.find((x) => x.key === key);
  if (!field) throw new Error(`no field ${key} in the ${spec.entity} spec`);
  return field;
}

/** The gate a field needs in a given mode: its own, else the entity's create or update gate. */
export function fieldGate(spec: EntitySpec, field: FieldSpec, mode: 'create' | 'update'): Gate {
  return field.gate ?? (mode === 'create' ? spec.createGate : spec.updateGate);
}

const NUMBER_RE = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d{1,3})?$/;

function decimals(s: string): number {
  const m = /\.(\d+)$/.exec(s.replace(/[eE].*$/, ''));
  return m ? m[1].replace(/0+$/, '').length : 0;
}

function rangeError(n: number, min: number, max: number): FieldError | null {
  return n < min || n > max ? { key: 'validation.range', values: { min, max } } : null;
}

function localYear(ctx: ValidationContext): number {
  return Number(localToday(ctx.timeZone, ctx.now).slice(0, 4));
}

/**
 * The client check for one field (on blur and on submit). `raw` is the form string; an empty value
 * is only an error for a required field. Mirrors the normalizer of the field's kind.
 */
export function validateField(field: FieldSpec, raw: string, ctx: ValidationContext): FieldError | null {
  const s = (raw ?? '').trim();
  if (s === '') return field.required ? { key: 'validation.required' } : null;
  if (field.max !== undefined && s.length > field.max) {
    return { key: 'validation.tooLong', values: { max: field.max } };
  }

  switch (field.kind) {
    case 'text':
    case 'longText':
    case 'uuid':
    case 'enum':
      if (field.kind === 'enum' && field.options && !field.options.includes(s)) return { key: 'validation.required' };
      return null;
    case 'authority': {
      const digits = s.replace(/[\s#-]/g, '').toUpperCase().replace(/^(USDOT|DOT|MC)/, '');
      return /^\d{1,8}$/.test(digits) ? null : { key: 'validation.authority' };
    }
    case 'ein':
      return /^\d{9}$/.test(s.replace(/[\s-]/g, '')) ? null : { key: 'validation.ein' };
    case 'state':
      return /^[A-Za-z]{2}$/.test(s) ? null : { key: 'validation.state' };
    case 'postal':
      return /^[0-9A-Za-z -]+$/.test(s) ? null : { key: 'validation.postal' };
    case 'email':
      return /^[^@\s]+@[^@\s]+$/.test(s) ? null : { key: 'validation.email' };
    case 'phone':
      return /^[0-9+() .-]+$/.test(s) ? null : { key: 'validation.phone' };
    case 'vin':
      return /^[A-HJ-NPR-Z0-9]{17}$/.test(s.replace(/\s/g, '').toUpperCase()) ? null : { key: 'validation.vin' };
    case 'license':
      return /^[A-Z0-9-]+$/.test(s.toUpperCase()) ? null : { key: 'validation.license' };
    case 'prefix':
      // app.carrier_clean: trim, upper, ^[A-Z0-9]{1,10}$.
      return /^[A-Z0-9]{1,10}$/.test(s.toUpperCase()) ? null : { key: 'validation.prefix' };
    case 'bool':
      return null;
    case 'website':
      return /\s/.test(s) ? { key: 'validation.noSpaces' } : null;
    case 'date': {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || Number.isNaN(new Date(`${s}T00:00:00Z`).getTime())) {
        return { key: 'validation.dateFormat' };
      }
      return s > localToday(ctx.timeZone, ctx.now) ? { key: 'validation.dateInFuture' } : null;
    }
    case 'int':
    case 'year': {
      if (!NUMBER_RE.test(s)) return { key: 'validation.wholeNumber' };
      const n = Number(s);
      if (!Number.isInteger(n)) return { key: 'validation.wholeNumber' };
      if (field.kind === 'year') return rangeError(n, 1980, localYear(ctx) + 1);
      return rangeError(n, field.min ?? -Infinity, field.maxValue ?? Infinity);
    }
    case 'num': {
      if (!NUMBER_RE.test(s)) return { key: 'validation.number' };
      const scale = field.scale ?? 2;
      if (decimals(s) > scale) return { key: 'validation.decimals', values: { scale } };
      return rangeError(Number(s), field.min ?? -Infinity, field.maxValue ?? Infinity);
    }
  }
}

/**
 * Every client error of a form, keyed by field. A field whose gate is denied is skipped: it is
 * disabled, never sent, so it cannot block the save.
 */
export function validateAll(
  spec: EntitySpec,
  values: FormValues,
  ctx: ValidationContext,
  can: Can,
  mode: 'create' | 'update',
): Record<string, FieldError> {
  const out: Record<string, FieldError> = {};
  for (const field of spec.fields) {
    if (!(field.key in values)) continue;
    if (!can(fieldGate(spec, field, mode))) continue;
    const err = validateField(field, values[field.key], ctx);
    if (err) out[field.key] = err;
  }
  return out;
}
